import type { BaseOptionType } from "antd/lib/select"

import {
  FINANCE_ITEM_TYPES,
  LINE_KINDS,
  type LineKind
} from "../../../../constants/finance-item-constants.ts"
import { RESPONSIBLE_TYPES } from "../../../../constants/responsible-type-constants.ts"

/**
 * "Material" is one word in the Source/Target picker and two fields underneath:
 * `line_kind` is what makes the line carry a material, and `responsible_type`
 * is whoever the line is with. The sentinel is a string so it can never collide
 * with a `ResponsibleType`, all of which are numbers.
 */
export const MATERIAL_SOURCE_VALUE = "material"

export type SourceTargetValue = number | typeof MATERIAL_SOURCE_VALUE | null

export interface SourceTargetPatch {
  responsible_type: number | null
  line_kind: LineKind | null
}

export const sourceTargetValue = (
  responsibleType: number | null | undefined,
  lineKind: number | null | undefined
): SourceTargetValue =>
  lineKind === LINE_KINDS.MATERIAL
    ? MATERIAL_SOURCE_VALUE
    : (responsibleType ?? null)

/**
 * Material is two independent flows, and the transaction type decides which:
 * a receivable sells the material to the customer, a payable buys it from the
 * supplier. Neither derives from the other.
 */
export const sourceTargetPatch = (
  value: SourceTargetValue | undefined,
  itemType?: number | null
): SourceTargetPatch =>
  value === MATERIAL_SOURCE_VALUE
    ? {
        responsible_type: materialResponsibleType(itemType),
        line_kind: LINE_KINDS.MATERIAL
      }
    : { responsible_type: (value as number | null) ?? null, line_kind: null }

export const materialResponsibleType = (
  itemType: number | null | undefined
): number =>
  itemType === FINANCE_ITEM_TYPES.PAYABLE
    ? RESPONSIBLE_TYPES.SUPPLIER
    : RESPONSIBLE_TYPES.CUSTOMER

// An EXPENSE line is rejected by the API unless it's a non-planned payable, so
// the Source/Target picker disables it elsewhere instead of letting the save 400.
export const EXPENSE_DISABLED_REASON = "Available only on actual payable lines."

// A Receivable belongs to a customer or a reimbursement (a sourceless group the
// summary counts); on any other contact it never reaches the financial summary,
// so block it here instead of letting the save 400.
export const RECEIVABLE_DISABLED_REASON =
  "Available only for customer or reimbursement sources."

// The mirror: a customer only owes the project, so it can never be a payable.
export const PAYABLE_DISABLED_REASON = "Not available for customer contacts."

export const isExpenseAllowed = (
  itemType: number | null | undefined,
  isPlanned: boolean | null | undefined
): boolean => itemType === FINANCE_ITEM_TYPES.PAYABLE && !isPlanned

export const isReceivableAllowed = (
  responsibleType: number | null | undefined
): boolean =>
  responsibleType == null ||
  responsibleType === RESPONSIBLE_TYPES.CUSTOMER ||
  responsibleType === RESPONSIBLE_TYPES.REIMBURSEMENT

export const isPayableAllowed = (
  responsibleType: number | null | undefined
): boolean => responsibleType !== RESPONSIBLE_TYPES.CUSTOMER

export const isResponsibleTypeAllowedForItemType = (
  responsibleType: number | null | undefined,
  itemType: number | null | undefined
): boolean => {
  if (itemType === FINANCE_ITEM_TYPES.RECEIVABLE)
    return isReceivableAllowed(responsibleType)
  if (itemType === FINANCE_ITEM_TYPES.PAYABLE)
    return isPayableAllowed(responsibleType)
  return true
}

/**
 * `materialOption` is opt-in, and only the Finance tab opts in.
 *
 * `ProjectViewMilestoneFinanceItemRow` emits a fixed `<td>` sequence with no
 * material cells, so offering Material there would hand the user a line they
 * cannot fill in — and any cell added to that row to fix it slides every
 * following field one column away from its heading.
 */
export const buildResponsibleTypeOptions = (
  baseOptions: BaseOptionType[] | undefined,
  expenseAllowed: boolean,
  itemType?: number | null,
  materialOption?: { label: string } | null
): BaseOptionType[] => {
  const options = (baseOptions ?? []).map((option) => {
    if (option.value === RESPONSIBLE_TYPES.EXPENSE) {
      return { ...option, disabled: !expenseAllowed }
    }
    return isResponsibleTypeAllowedForItemType(option.value, itemType)
      ? option
      : { ...option, disabled: true }
  })

  if (!materialOption) return options

  // Material is offered on both sides — selling it to the customer and buying
  // it from the supplier are separate flows, so neither transaction type rules
  // the other out.
  return [
    ...options,
    { label: materialOption.label, value: MATERIAL_SOURCE_VALUE }
  ]
}

export const responsibleTypeDisabledReason = (
  responsibleType: number | string | null | undefined,
  itemType: number | null | undefined,
  expenseAllowed: boolean
): string => {
  if (responsibleType === RESPONSIBLE_TYPES.EXPENSE && !expenseAllowed)
    return EXPENSE_DISABLED_REASON
  if (itemType === FINANCE_ITEM_TYPES.RECEIVABLE)
    return RECEIVABLE_DISABLED_REASON
  if (itemType === FINANCE_ITEM_TYPES.PAYABLE) return PAYABLE_DISABLED_REASON
  return EXPENSE_DISABLED_REASON
}

export const buildItemTypeOptions = (
  baseOptions: BaseOptionType[] | undefined,
  receivableAllowed: boolean,
  payableAllowed: boolean
): BaseOptionType[] =>
  (baseOptions ?? []).map((option) => {
    if (option.value === FINANCE_ITEM_TYPES.RECEIVABLE) {
      return { ...option, disabled: !receivableAllowed }
    }
    if (option.value === FINANCE_ITEM_TYPES.PAYABLE) {
      return { ...option, disabled: !payableAllowed }
    }
    return option
  })
